import { styled, useTheme } from '@mui/material';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { logGoogleAnalyticsEvent, GoogleAnalyticsEvent } from 'components/utils/ga';
import { formatDate } from '../../../../utils/date_time';

export interface AssignedForm {
  title: string;
  url: string;
  submitted_on: string;
  publication_date: string;
  id: string;
}

interface Props {
  forms: Array<AssignedForm>;
}

const StyledHeaderTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 'bold',
    paddingTop: '8px',
    paddingBottom: '8px',
    fontSize: '16px'
  }
}));

const StyledContentTableCell = styled(TableCell)(() => ({
  fontSize: '16px'
}));

const NUM_TABLE_COLUMNS = 3;

function FormTableHeader(): React.ReactElement {
  return (
    <TableRow>
      <StyledHeaderTableCell key="formTitle" sx={{ paddingLeft: '24px' }}>
        Form Title
      </StyledHeaderTableCell>
      <StyledHeaderTableCell key="submittedOn">
        Submitted On
      </StyledHeaderTableCell>
    </TableRow>
  );
}

interface TableRowProps {
  form: AssignedForm;
}

const trackFormClick = (form: AssignedForm) => {
  logGoogleAnalyticsEvent(GoogleAnalyticsEvent.FormAccess, {
    event_category: 'form_navigation',
    event_label: 'assigned_form_table',
    form_id: form.url.split('/').pop()
  });
};

function FormTableRow({ form }: TableRowProps): React.ReactElement {
  return (
    <TableRow hover tabIndex={-1}>
      <StyledContentTableCell sx={{ pl: 3, py: 2 }}>
        <Link
          href={form.url}
          onClick={() => trackFormClick(form)}
          sx={{ lineHeight: '24px' }}
        >
          <strong>{form.title}</strong>
        </Link>
      </StyledContentTableCell>
      <StyledContentTableCell>
        {!form.submitted_on ? 'Not submitted' : formatDate(form.submitted_on)}
      </StyledContentTableCell>
    </TableRow>
  );
}

export default function FormsTable({ forms }: Props): React.ReactElement {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  const sortedForms = sortForms(forms);

  return (
    <Paper sx={{ width: '100%' }}>
      <TableContainer
        sx={{
          overflow: 'auto',
          maxHeight: '100%',
          borderRadius: '4px'
        }}
      >
        <Table size="small" stickyHeader aria-label="sticky table">
          <TableHead>
            <FormTableHeader />
          </TableHead>
          <TableBody>{renderTableBody(sortedForms)}</TableBody>
        </Table>
      </TableContainer>

      {forms.length !== 0 && (
        <TablePagination
          rowsPerPageOptions={[8, 15, 25, 50, 100]}
          component="div"
          count={forms.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          padding="none"
          sx={{
            // We override styles for smaller screens so that the pagination component does not overflow horizontally.
            // The margins for larger screens are exactly the default margins.
            '.MuiTablePagination-input': {
              marginLeft: { xs: 0, lg: theme.spacing(1) },
              marginRight: { xs: theme.spacing(2.5), lg: theme.spacing(4) }
            }
          }}
        />
      )}
    </Paper>
  );

  function handleChangePage(event: unknown, newPage: number) {
    setPage(newPage);
  }

  function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }

  function renderTableBody(forms: Array<AssignedForm>) {
    if (forms.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={NUM_TABLE_COLUMNS}>
            <Stack alignItems="center" justifyContent="center" paddingY={9}>
              <Typography variant="body1">No forms found.</Typography>
            </Stack>
          </TableCell>
        </TableRow>
      );
    }

    return forms
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map(form => <FormTableRow key={form.url} form={form} />);
  }

  function sortForms(forms: Array<AssignedForm>): Array<AssignedForm> {
    return [...forms].sort((a, b) => {
      const submittedOnA = getDateComparisonValue(a.submitted_on);
      const submittedOnB = getDateComparisonValue(b.submitted_on);

      // If both are null/undefined, compare by publication date
      if (submittedOnA === Number.MAX_SAFE_INTEGER &&
          submittedOnB === Number.MAX_SAFE_INTEGER) {
        return getDateComparisonValue(b.publication_date) -
               getDateComparisonValue(a.publication_date);
      }

      return submittedOnB - submittedOnA;
    });
  }

  function getDateComparisonValue(dateValue: string | null | undefined): number {
    if (!dateValue) {
      return Number.MAX_SAFE_INTEGER;
    }

    return new Date(dateValue).getTime();
  }
}
